/**
 * The hotbar — what is in your hands, and what you can put there, on the
 * bottom edge of a fight.
 *
 * Janeks, 2026-09-19: *"show a bottom ui block with equipment, 1-9,0 - slots
 * with equipment, press the ui slot (on mobile - touch) OR the corresponding
 * number on keyboard and it is equiped or used, e.g. - hands = 0, primary 1,
 * secondary 2, bandage 3 and etc."*
 *
 * ─── The positions are FIXED, and empties are still drawn ───────────────────
 * Hands are always 0, the PRIMARY is always 1, the SECONDARY always 2,
 * whether or not anything is in them. Packing the bar (so an empty PRIMARY
 * slides the sidearm onto key 1) would make the binding depend on the
 * loadout, and the only thing a bar of numbered cells is for is that the
 * number stops being something you read. A cell with nothing in it renders as
 * an empty well and refuses to be selected.
 *
 * Consumables fill 3 upwards, grouped by item so three separate bandage rows
 * are one cell reading x3 rather than three cells. Ten cells is the whole bar
 * — there is no page two, and `HOTBAR_SIZE` is the cap the builder truncates
 * to rather than a layout that reflows.
 *
 * ─── A weapon with no ammo is a legal choice ────────────────────────────────
 * *"You can select a weapon with NO ammo! e.g. You use all of it. then When
 * shooting, play error sound, highlight the selected UI slot in RED and show
 * a red notice text 'OUT OF AMMO' in the middle. Dont revert to hands on
 * 'shot'."*
 *
 * So an empty weapon is selectable, stays selected, and says so when fired.
 * `flashError` is that feedback's visual half; the sound is the caller's,
 * because the mixer belongs to the view. The room enforces the same rule from
 * the other side — it refuses the shot and sends `outOfAmmo` instead of
 * quietly swapping in bare hands, which is what it used to do.
 *
 * ─── Plain DOM, no panel registry ───────────────────────────────────────────
 * Same reasoning as `duel-hud.ts`: the arena is its own mounted view with no
 * other windows to arbitrate against, so registering here would leave a
 * registration behind for a torn-down element.
 */
import type { InventorySheet, EquipmentSheet } from '../../state/game-state.types';
import type { WeaponSlotId } from '../../state/battleground-state.types';

import { pictureHtml } from './paperdoll';

/** Cells on the bar: keys 1-9 then 0. The digit is the index, so 0 sorts last on screen and first in the model. */
export const HOTBAR_SIZE = 10;

/**
 * Bare hands, as a real icon.
 *
 * Supplied by Janeks on 2026-09-19 and run through the project's own
 * `assets/generated_items/build_multisize_icons.py --from` — so it ships the
 * same three tiers (`_icon_slot` 64px / `_icon` 256px / `_icon_full` 512px)
 * as every other item icon, and `pictureHtml` picks the right one for the
 * cell's 44px box exactly as it does for a shotgun.
 *
 * Referenced by FILENAME, not by an ItemDef: bare hands are the one hotbar
 * entry with no catalog row behind them, so there is nothing to seed an
 * `iconPath` onto. The name still follows the catalog's convention, because
 * `iconTiers` derives the other two tiers by string-rewriting `_icon.` —
 * anything else here would silently render the detail tier at all three sizes.
 */
const FISTS_ICON = 'fists_icon.webp';

export interface HotbarSlot {
  /** 0-9. Both the keyboard binding and the digit drawn in the cell's corner. */
  index: number;
  /**
   * `WEAPON` cells swap what is in your hands; `CONSUMABLE` cells burn one
   * item. `EMPTY` is a drawn-but-dead cell — see the fixed-positions note.
   */
  kind: 'WEAPON' | 'CONSUMABLE' | 'EMPTY';
  /** WEAPON only — which of the three the room should put in this fighter's hands. */
  weaponSlot?: WeaponSlotId;
  /** CONSUMABLE only — the `UserItem` row to burn. The FIRST of a stacked group. */
  userItemId?: string;
  /** CONSUMABLE only — the def key every row in this group shares. */
  itemKey?: string;
  label: string;
  iconPath: string | null;
  /** Units behind this cell. Drawn only above 1 — an x1 badge on every cell is noise. */
  quantity: number;
}

export interface HotbarOptions {
  /** Keys 0/1/2 — `sendSelectSlot`. */
  onSelectWeapon(slot: WeaponSlotId): void;
  /** Keys 3-9 — `sendUseItem`. */
  onUseItem(userItemId: string): void;
}

export interface HotbarHandles {
  /** Re-render from a fresh loadout. Cheap enough to call on every store push. */
  setSlots(slots: readonly HotbarSlot[]): void;
  /** Which weapon cell reads as held. Driven by the ROOM's synced state, never optimistically. */
  setActive(slot: WeaponSlotId): void;
  /** Rounds left under the held weapon. `-1`/undefined means "no magazine concept" and draws nothing. */
  setAmmo(remaining: number | undefined): void;
  /** Fire the cell bound to `index`, exactly as a click or a keypress would. Returns false if the cell is dead. */
  trigger(index: number): boolean;
  /** The red cell + the red centre notice. Both clear themselves. */
  flashError(message: string): void;
  /**
   * Draw (or clear) the heal cast bar.
   *
   * `remainingMs` comes from the ROOM's own `healingRemaining`, not from a
   * local timer — a `setInterval` started on `healStarted` drifts from the
   * server's clock and finishes at a different moment than the heal does.
   * Pass `null` to clear.
   */
  setHealing(state: { itemName: string; remainingMs: number; durationMs: number } | null): void;
  /** For tests and for the view's own key handler. */
  readonly root: HTMLElement;
  dispose(): void;
}

/**
 * `KeyboardEvent.key` -> cell index, or `null` for anything else.
 *
 * Reads `key`, not `code`, so the digits work on a numpad and on a layout
 * where they are not where a US keyboard puts them. Deliberately rejects
 * modified presses: Ctrl+1 is a browser tab switch and Alt+2 is an OS
 * shortcut on several platforms, and swallowing either would be worse than
 * not binding it.
 */
export function hotbarIndexForKey(e: Pick<KeyboardEvent, 'key' | 'ctrlKey' | 'metaKey' | 'altKey'>): number | null {
  if (e.ctrlKey || e.metaKey || e.altKey) return null;
  if (e.key.length !== 1 || e.key < '0' || e.key > '9') return null;
  return Number(e.key);
}

/**
 * The loadout, as ten cells.
 *
 * Pure — no DOM, no stores, no network. It takes the two sheets the client
 * already holds and returns what the bar should look like, which is the half
 * worth testing: "key 2 selects the sidearm" is a fact about this function,
 * not about a click handler.
 *
 * ─── Why the client builds this and not the server ──────────────────────────
 * The room already knows every weapon (it resolved all of them at build time,
 * see `resolvePlayerWeapons`), but it would have to sync the bar per-fighter
 * for the client to read it, and a Colyseus schema is shared by both
 * duelists — a bar synced on `PlayerState` would hand your opponent your
 * bandage count. The client has the equipment and inventory sheets already,
 * so it builds the bar and sends only the CHOICE; the room re-resolves and
 * re-validates every one (`handleSelectSlot`, `handleUseItem`).
 */
export function buildHotbarSlots(
  equipment: EquipmentSheet | null | undefined,
  inventory: InventorySheet | null | undefined,
): HotbarSlot[] {
  const slots: HotbarSlot[] = [
    {
      index: 0, kind: 'WEAPON', weaponSlot: 'UNARMED', label: 'Hands', iconPath: FISTS_ICON, quantity: 1,
    },
  ];

  for (const [index, slotId] of ([[1, 'PRIMARY'], [2, 'SECONDARY']] as const)) {
    const equipped = equipment?.slots?.[slotId];
    // `kind` lives on the item DEFINITION, not on the equipped instance —
    // and armour is equippable in other slots, so this is a real filter and
    // not a formality. A non-weapon in a weapon slot draws an empty cell,
    // because the room would refuse to select it anyway.
    const isWeapon = equipped?.def.kind === 'MELEE' || equipped?.def.kind === 'FIREARM';
    slots.push(isWeapon && equipped
      ? {
        index,
        kind: 'WEAPON',
        weaponSlot: slotId,
        label: equipped.def.name,
        iconPath: equipped.def.iconPath,
        quantity: 1,
      }
      : {
        index, kind: 'EMPTY', label: slotId === 'PRIMARY' ? 'Primary' : 'Sidearm', iconPath: null, quantity: 0,
      });
  }

  // ─── Consumables, grouped ─────────────────────────────────────────────────
  // By def key, not by row: a bag holding three separate `bandage` rows is one
  // cell reading x3. `usable` is the backend's own "has a populated
  // consumableEffect" flag, so a decorative CONSUMABLE with no effect never
  // takes a key that would do nothing when pressed.
  //
  // CARRIED only. Hideout stock was left at home, which is the same line
  // `countCarriedAmmo` draws for rounds.
  const grouped = new Map<string, HotbarSlot>();
  for (const item of inventory?.carried ?? []) {
    if (!item.usable) continue;
    const existing = grouped.get(item.itemId);
    if (existing) {
      existing.quantity += item.quantity;
      continue;
    }
    grouped.set(item.itemId, {
      index: 0, // assigned below, once the order is known
      kind: 'CONSUMABLE',
      userItemId: item.id,
      itemKey: item.itemId,
      label: item.name,
      iconPath: item.iconPath,
      quantity: item.quantity,
    });
  }

  // Sorted by name so the bar is STABLE. Insertion order would follow whatever
  // order the sheet happened to arrive in, and a bandage that moves from key 3
  // to key 4 between two pushes is worse than no binding at all.
  const consumables = [...grouped.values()].sort((a, b) => a.label.localeCompare(b.label));
  for (const slot of consumables) {
    if (slots.length >= HOTBAR_SIZE) break;
    slot.index = slots.length;
    slots.push(slot);
  }

  // Pad to a full bar. Ten cells always, so the row does not change width as
  // the last bandage is used — a bar that resizes under the cursor is a bar
  // you cannot click reliably.
  while (slots.length < HOTBAR_SIZE) {
    slots.push({
      index: slots.length, kind: 'EMPTY', label: '', iconPath: null, quantity: 0,
    });
  }
  return slots;
}

/** The digit drawn in a cell's corner — key 0 is the tenth cell, not the first. */
export function keycapFor(index: number): string {
  return String(index);
}

/** Stack badge text, or '' when there is nothing worth saying. */
export function quantityBadge(slot: HotbarSlot): string {
  return slot.kind === 'CONSUMABLE' && slot.quantity > 1 ? `x${slot.quantity}` : '';
}

/** How long the red cell and the centre notice stay up. Long enough to read, short enough not to sit over the fight. */
const ERROR_HOLD_MS = 1200;

export function createHotbar(container: HTMLElement, opts: HotbarOptions): HotbarHandles {
  const root = document.createElement('div');
  root.className = 'bv-hotbar';

  const cells = document.createElement('div');
  cells.className = 'bv-hotbar-cells';
  root.appendChild(cells);

  // The centre notice lives in this module rather than in the HUD because it
  // is the other half of the red cell — one call, one piece of feedback, and
  // nothing to keep in step across two files.
  const notice = document.createElement('div');
  notice.className = 'bv-hotbar-notice';
  notice.setAttribute('role', 'status');
  root.appendChild(notice);

  // ─── The heal cast bar ──────────────────────────────────────────────────
  // Janeks: *"Healing action, should take 3 seconds to heal ligtly, 5 -
  // medium healthkit, 8 for large medkit."* A duration the player cannot see
  // running is indistinguishable from a keypress that did nothing, so the bar
  // is not decoration — it is the feature. Sits just above the cells, where
  // the eye already is after pressing a number.
  const cast = document.createElement('div');
  cast.className = 'bv-hotbar-cast';
  const castLabel = document.createElement('div');
  castLabel.className = 'bv-hotbar-cast-label';
  const castTrack = document.createElement('div');
  castTrack.className = 'bv-hotbar-cast-track';
  const castFill = document.createElement('div');
  castFill.className = 'bv-hotbar-cast-fill';
  castTrack.appendChild(castFill);
  cast.append(castLabel, castTrack);
  root.insertBefore(cast, cells);

  container.appendChild(root);

  let slots: HotbarSlot[] = [];
  let active: WeaponSlotId = 'UNARMED';
  let ammo: number | undefined;
  let errorTimer: ReturnType<typeof setTimeout> | null = null;

  const render = (): void => {
    cells.innerHTML = slots.map((slot) => {
      const classes = ['bv-hotbar-cell'];
      if (slot.kind === 'EMPTY') classes.push('is-empty');
      if (slot.kind === 'WEAPON' && slot.weaponSlot === active) classes.push('is-active');
      const art = pictureHtml(slot.iconPath, slot.label, 'bv-hotbar-art', '44px');
      const badge = quantityBadge(slot);
      // Ammo rides the HELD weapon cell only: a rifle's round count under a
      // holstered sidearm would be someone else's number.
      const showAmmo = slot.kind === 'WEAPON' && slot.weaponSlot === active
        && ammo !== undefined && ammo >= 0;
      return [
        `<button type="button" class="${classes.join(' ')}" data-hotbar="${slot.index}"`,
        ` aria-label="${escapeAttr(slot.label || 'Empty slot')}"`,
        slot.kind === 'EMPTY' ? ' disabled' : '',
        '>',
        `<span class="bv-hotbar-key">${keycapFor(slot.index)}</span>`,
        art,
        badge ? `<span class="bv-hotbar-qty">${badge}</span>` : '',
        showAmmo ? `<span class="bv-hotbar-ammo">${ammo}</span>` : '',
        '</button>',
      ].join('');
    }).join('');
  };

  const trigger = (index: number): boolean => {
    const slot = slots.find((s) => s.index === index);
    if (!slot || slot.kind === 'EMPTY') return false;
    if (slot.kind === 'WEAPON' && slot.weaponSlot) {
      opts.onSelectWeapon(slot.weaponSlot);
      return true;
    }
    if (slot.kind === 'CONSUMABLE' && slot.userItemId) {
      opts.onUseItem(slot.userItemId);
      return true;
    }
    return false;
  };

  // ONE delegated listener rather than one per cell: `render` replaces the
  // whole row on every store push, and per-cell listeners would have to be
  // torn down and rebound each time (or leak).
  const onClick = (e: Event): void => {
    const button = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-hotbar]');
    if (!button) return;
    const index = Number(button.dataset.hotbar);
    if (Number.isNaN(index)) return;
    trigger(index);
  };
  cells.addEventListener('click', onClick);

  const clearError = (): void => {
    if (errorTimer) { clearTimeout(errorTimer); errorTimer = null; }
    notice.textContent = '';
    notice.classList.remove('is-shown');
    for (const cell of cells.querySelectorAll('.is-error')) cell.classList.remove('is-error');
  };

  return {
    root,
    setSlots(next) {
      slots = [...next];
      render();
    },
    setActive(next) {
      if (next === active) return;
      active = next;
      render();
    },
    setAmmo(remaining) {
      // -1 is the room's "no magazine concept" sentinel, not "zero rounds" —
      // see `PlayerState.ammoRemaining`. Normalised to undefined here so every
      // reader below only has to know about one absent value.
      const normalised = remaining === undefined || remaining < 0 ? undefined : remaining;
      if (normalised === ammo) return;
      ammo = normalised;
      render();
    },
    trigger,
    setHealing(state) {
      if (!state || state.durationMs <= 0) {
        cast.classList.remove('is-shown');
        return;
      }
      // Elapsed, not remaining: a bar that FILLS reads as progress toward the
      // heal. A draining one reads as time running out, which is the opposite
      // of what is happening.
      const elapsed = Math.max(0, state.durationMs - Math.max(0, state.remainingMs));
      cast.classList.add('is-shown');
      castLabel.textContent = `${state.itemName}  ${(state.remainingMs / 1000).toFixed(1)}s`;
      castFill.style.width = `${Math.min(100, (elapsed / state.durationMs) * 100)}%`;
    },
    flashError(message) {
      clearError();
      notice.textContent = message;
      notice.classList.add('is-shown');
      // The RED cell is the one currently held — that is the weapon that
      // refused to fire, and it is where the player is already looking.
      const held = slots.find((s) => s.kind === 'WEAPON' && s.weaponSlot === active);
      if (held) cells.querySelector(`[data-hotbar="${held.index}"]`)?.classList.add('is-error');
      errorTimer = setTimeout(clearError, ERROR_HOLD_MS);
    },
    dispose() {
      if (errorTimer) clearTimeout(errorTimer);
      cells.removeEventListener('click', onClick);
      root.remove();
    },
  };
}

/** Attribute-safe. Item names are catalog content, but they land in markup here. */
function escapeAttr(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
